const H1 = ({ children }) => (
  <h2 style={{
    fontFamily: '"Syne", sans-serif',
    fontSize: 20,
    fontWeight: 600,
    color: '#e2ecff',
    margin: '0 0 16px 0',
    lineHeight: 1.3,
    letterSpacing: '-0.01em',
  }}>
    {children}
  </h2>
);

const H2 = ({ children }) => (
  <h3 style={{
    fontFamily: '"JetBrains Mono", monospace',
    fontSize: 10,
    fontWeight: 500,
    color: '#a78bfa',
    margin: '24px 0 8px 0',
    letterSpacing: '0.15em',
    textTransform: 'uppercase',
  }}>
    {children}
  </h3>
);

const P = ({ children, lead = false }) => (
  <p style={{
    fontFamily: '"Inter", sans-serif',
    fontSize: lead ? 14 : 13,
    color: lead ? '#e2ecff' : '#8892b0',
    lineHeight: 1.75,
    margin: '0 0 12px 0',
  }}>
    {children}
  </p>
);

const Emphasis = ({ children, color = '#22d3ee' }) => (
  <strong style={{ color, fontWeight: 600 }}>{children}</strong>
);

const Quote = ({ children, source }) => (
  <div style={{
    margin: '16px 0',
    padding: '12px 16px',
    background: 'rgba(167, 139, 250, 0.06)',
    borderLeft: '2px solid #a78bfa',
    borderRadius: '0 4px 4px 0',
  }}>
    <div style={{
      fontFamily: '"Inter", sans-serif',
      fontSize: 13,
      fontStyle: 'italic',
      color: '#e2ecff',
      lineHeight: 1.7,
    }}>
      {children}
    </div>
    {source && (
      <div style={{
        fontFamily: '"JetBrains Mono", monospace',
        fontSize: 10,
        color: '#4a5578',
        marginTop: 6,
        letterSpacing: '0.05em',
      }}>
        {source}
      </div>
    )}
  </div>
);

const List = ({ items, marker = '·', markerColor = '#22d3ee' }) => (
  <ul style={{ margin: '0 0 12px 0', padding: 0, listStyle: 'none' }}>
    {items.map((item, i) => (
      <li key={i} style={{
        display: 'flex',
        gap: 10,
        marginBottom: 6,
        fontFamily: '"Inter", sans-serif',
        fontSize: 13,
        color: '#8892b0',
        lineHeight: 1.7,
      }}>
        <span style={{ color: markerColor, flexShrink: 0, fontWeight: 600 }}>{marker}</span>
        <span>{item}</span>
      </li>
    ))}
  </ul>
);

const Callout = ({ children, type = 'info' }) => {
  const colors = {
    info: { border: '#22d3ee', bg: 'rgba(34, 211, 238, 0.06)', label: 'À NOTER' },
    warning: { border: '#fbbf24', bg: 'rgba(251, 191, 36, 0.06)', label: 'ATTENTION' },
    success: { border: '#34d399', bg: 'rgba(52, 211, 153, 0.06)', label: 'POINT CLÉ' },
  };
  const c = colors[type] || colors.info;
  return (
    <div style={{
      margin: '16px 0',
      padding: '10px 14px',
      background: c.bg,
      borderLeft: `2px solid ${c.border}`,
      borderRadius: '0 4px 4px 0',
    }}>
      <div style={{
        fontFamily: '"JetBrains Mono", monospace',
        fontSize: 9,
        color: c.border,
        letterSpacing: '0.15em',
        marginBottom: 6,
      }}>
        {c.label}
      </div>
      <div style={{
        fontFamily: '"Inter", sans-serif',
        fontSize: 12,
        color: '#e2ecff',
        lineHeight: 1.7,
      }}>
        {children}
      </div>
    </div>
  );
};

const Example = ({ children, label }) => (
  <div style={{
    margin: '14px 0',
    padding: '10px 14px',
    background: '#080814',
    border: '1px solid rgba(167, 139, 250, 0.15)',
    borderRadius: 4,
  }}>
    {label && (
      <div style={{
        fontFamily: '"JetBrains Mono", monospace',
        fontSize: 9,
        color: '#4a5578',
        letterSpacing: '0.15em',
        marginBottom: 6,
      }}>
        {label}
      </div>
    )}
    <div style={{
      fontFamily: '"Inter", sans-serif',
      fontSize: 12,
      color: '#8892b0',
      lineHeight: 1.7,
    }}>
      {children}
    </div>
  </div>
);

const AxisRef = ({ children, color }) => (
  <span style={{
    fontFamily: '"JetBrains Mono", monospace',
    fontSize: 11,
    color: color || '#a78bfa',
    background: (color || '#a78bfa') + '15',
    padding: '1px 6px',
    borderRadius: 3,
    letterSpacing: '0.03em',
  }}>
    {children}
  </span>
);

// ═══════════════════════════════════════════════════════════════════════════════
// MODULES
// ═══════════════════════════════════════════════════════════════════════════════

export const livretModules = [
  // ═══════════════════════════════════════════════════════════════════════════
  // MODULE 00 — Le modèle en une page
  // ═══════════════════════════════════════════════════════════════════════════
  {
    id: '00',
    title: 'Le modèle en une page',
    subtitle: 'Ce que l\'atlas des postures propose',
    profiles: ['apprenant', 'formateur', 'auditeur'],
    duration: '3 min',
    content: () => (
      <>
        <H1>Le modèle en une page</H1>

        <P lead>
          L'atlas ne vous dit pas qui vous êtes. Il ne vous dit pas non plus quelle posture est la bonne.
          Il propose une carte pour situer une manière d'être face à la complexité, et pour comprendre ce que cette
          manière d'être produit dans le temps.
        </P>

        <P>
          Le modèle repose sur trois éléments :
        </P>

        <List items={[
          <><Emphasis>Six axes</Emphasis> qui décrivent les dimensions fondamentales d'une posture.</>,
          <><Emphasis>Des positions</Emphasis> dans cet espace, qui correspondent à des manières d'être cohérentes.</>,
          <><Emphasis>Des trajectoires</Emphasis> qui relient ces positions, avec leurs conditions et leurs obstacles.</>,
        ]} />

        <Callout type="success">
          Un point dans l'espace c'est une <Emphasis>manière d'être</Emphasis>, qui peut
          évoluer. L'atlas existe pour éclairer.
        </Callout>

        <H2>Points importants</H2>

        <List items={[
          <>Ceci n'est pas un test de personnalité. Les postures sont des régimes d'action, rien n'est figé.</>,
          <>Il n'y a pas de jugement. Une posture n'est ni bonne ni mauvaise en soi. Elle produit des effets dans un contexte donné.</>,
          <>L'atlas n'est pas conçu comme un outil de notation. Il ne donne pas de score, il propose une lecture.</>,
        ]} marker="—" markerColor="#4a5578" />

        <H2>Comment lire l'atlas</H2>

        <P>
          Trois modes de lecture sont disponibles dans l'interface, accessibles depuis l'en-tête.
          Le mode <Emphasis color="#a78bfa">Atelier</Emphasis> sert à explorer. Le mode{' '}
          <Emphasis color="#f97316">Scénario</Emphasis> sert à comprendre ce que chaque posture produit dans une
          situation donnée. Le mode <Emphasis color="#22d3ee">Trace</Emphasis> sert à enregistrer une trajectoire
          pour la documenter.
        </P>

        <P>
          Chaque élément de l'atlas — un axe, une posture, une trajectoire, un scénario — peut être inspecté en
          le sélectionnant. La colonne de droite affiche alors une fiche détaillée, organisée par onglets quand
          plusieurs lectures sont possibles.
        </P>
      </>
    ),
  },

  // ═══════════════════════════════════════════════════════════════════════════
  // MODULE 01 — Les six axes
  // ═══════════════════════════════════════════════════════════════════════════
  {
    id: '01',
    title: 'Les six axes',
    subtitle: 'Les dimensions d\'une posture',
    profiles: ['apprenant', 'formateur', 'auditeur'],
    duration: '5 min',
    content: () => (
      <>
        <H1>Les six axes</H1>

        <P lead>
          Une posture face à la complexité ne se réduit pas à une seule dimension. Elle se décrit par la
          combinaison de six axes. Chaque axe est un <Emphasis>spectre</Emphasis>, pas une opposition binaire.
        </P>

        <H2>Axe 1 — Posture face à l'incertitude</H2>

        <P>
          Comment vous situez-vous face à ce que vous ne contrôlez pas ? Deux pôles structurent cet axe :
        </P>

        <List items={[
          <><AxisRef color="#ef4444">Contrôle</AxisRef> — chercher à maîtriser, prévoir, réduire l'incertitude.</>,
          <><AxisRef color="#22d3ee">Navigation</AxisRef> — composer avec, s'ajuster, lire les signaux.</>,
        ]} />

        <P>
          La question épistémologique qui traverse cet axe : <Emphasis>face à ce que je ne maîtrise pas,
          est-ce que je serre, ou est-ce que j'écoute ?</Emphasis>
        </P>

        <H2>Axe 2 — Rapport au temps</H2>

        <List items={[
          <><AxisRef color="#f97316">Court terme</AxisRef> — réagir, corriger, optimiser dans l'instant.</>,
          <><AxisRef color="#a78bfa">Temps long</AxisRef> — construire, cultiver, laisser mûrir.</>,
        ]} />

        <P>
          Un acteur peut décider vite sans pour autant décider juste. Un acteur peut décider lentement sans pour
          autant décider durablement. Cet axe mesure l'<Emphasis>horizon</Emphasis>, pas la qualité.
        </P>

        <H2>Axe 3 — Rapport à l'autre</H2>

        <List items={[
          <><AxisRef color="#ef4444">Contrôler</AxisRef> — diriger, prescrire, encadrer.</>,
          <><AxisRef color="#34d399">Accompagner</AxisRef> — soutenir, révéler, laisser advenir.</>,
        ]} />

        <P>
          Cet axe décrit ce que vous cherchez à produire chez l'autre : la conformité, ou l'autonomie.
        </P>

        <H2>Axe 4 — Rapport à la mesure</H2>

        <List items={[
          <><AxisRef color="#fbbf24">Évaluer</AxisRef> — mesurer pour juger, classer, décider.</>,
          <><AxisRef color="#22d3ee">Documenter</AxisRef> — mesurer pour comprendre, garder trace, apprendre.</>,
        ]} />

        <P>
          Mesurer n'est jamais neutre. Un indicateur qui devient un objectif cesse d'être un bon indicateur.
          Cet axe décrit ce que vous cherchez à faire avec les chiffres.
        </P>

        <H2>Axe 5 — Rapport à l'erreur</H2>

        <List items={[
          <><AxisRef color="#ef4444">Sanctionner</AxisRef> — punir, corriger, éviter.</>,
          <><AxisRef color="#34d399">Accueillir</AxisRef> — écouter, comprendre, intégrer.</>,
        ]} />

        <P>
          L'erreur est le seul moyen dont dispose un système pour apprendre. La manière dont une organisation
          traite l'erreur détermine ce qu'elle peut voir.
        </P>

        <H2>Axe 6 — Rapport au système</H2>

        <List items={[
          <><AxisRef color="#94a3b8">Silos</AxisRef> — voir des éléments séparés.</>,
          <><AxisRef color="#a78bfa">Interactions</AxisRef> — voir des relations et des boucles.</>,
        ]} />

        <P>
          Ce que vous voyez quand vous regardez le monde détermine ce que vous pouvez y faire. Voir des objets
          conduit à optimiser localement. Voir des relations conduit à comprendre les effets différés.
        </P>

        <Callout type="info">
          Un acteur n'occupe jamais un pôle pur. Toute posture est une <Emphasis>position</Emphasis> sur chacun
          des six axes, simultanément.
        </Callout>
      </>
    ),
  },

  // ═══════════════════════════════════════════════════════════════════════════
  // MODULE 02 — Les postures
  // ═══════════════════════════════════════════════════════════════════════════
  {
    id: '02',
    title: 'Les postures',
    subtitle: 'Cohérences dans l\'espace des axes',
    profiles: ['apprenant', 'formateur', 'auditeur'],
    duration: '6 min',
    content: () => (
      <>
        <H1>Les postures</H1>

        <P lead>
          Une posture est une <Emphasis>cohérence</Emphasis> dans l'espace des six axes. Ce n'est pas une
          combinaison aléatoire : les positions qui tiennent dans le temps se ressemblent, et forment ce que
          l'atlas appelle des <Emphasis>archétypes</Emphasis>.
        </P>

        <H2>Pourquoi des archétypes et pas des catégories</H2>

        <P>
          Une catégorie enferme. Un archétype éclaire. L'archétype n'est pas une boîte dans laquelle on range un
          acteur, c'est une <Emphasis>configuration</Emphasis> de positions qui produit des effets caractéristiques.
        </P>

        <Example label="EXEMPLE">
          Un acteur proche de l'archétype <Emphasis color="#ef4444">Contrôleur</Emphasis> cherche à maîtriser
          chaque variable, prescrit plutôt qu'il n'accompagne, évalue plutôt qu'il ne documente, sanctionne
          l'erreur, et voit le monde en silos. Ce n'est pas un jugement moral — c'est une description de
          cohérence.
        </Example>

        <H2>Ce que chaque archétype contient</H2>

        <List items={[
          <><Emphasis>Une signature</Emphasis> — une phrase qui résume son rapport au monde.</>,
          <><Emphasis>Une phénoménologie</Emphasis> — comment le monde apparaît depuis cette position.</>,
          <><Emphasis>Ce que la posture produit</Emphasis> — les effets dans le temps.</>,
          <><Emphasis>Ses angles morts</Emphasis> — ce qu'elle ne voit pas, et qui la fragilise.</>,
        ]} />

        <H2>Ce que les archétypes ne sont pas</H2>

        <List items={[
          <>Des types de personnes. Une même personne peut occuper des postures différentes selon les contextes.</>,
          <>Des niveaux de valeur. Aucune posture n'est supérieure à une autre dans l'absolu.</>,
          <>Des destins. Les archétypes décrivent des <Emphasis>positions</Emphasis>, pas des <Emphasis>identités</Emphasis>.</>,
        ]} marker="—" markerColor="#4a5578" />

        <Callout type="warning">
          Une posture durable n'est pas une posture parfaite. C'est une posture qui tient dans le temps sans
          s'épuiser, et qui reste capable d'apprendre.
        </Callout>
      </>
    ),
  },

  // ═══════════════════════════════════════════════════════════════════════════
  // MODULE 03 — Les trajectoires
  // ═══════════════════════════════════════════════════════════════════════════
  {
    id: '03',
    title: 'Les trajectoires',
    subtitle: 'Déplacements entre postures',
    profiles: ['apprenant', 'formateur'],
    duration: '5 min',
    content: () => (
      <>
        <H1>Les trajectoires</H1>

        <P lead>
          Une trajectoire décrit un <Emphasis>déplacement</Emphasis> entre deux postures. Elle indique les
          conditions nécessaires, les obstacles prévisibles, et les étapes qui structurent le passage.
        </P>

        <H2>Anatomie d'une trajectoire</H2>

        <List items={[
          <><Emphasis>Le point de départ</Emphasis> — la posture que l'on quitte.</>,
          <><Emphasis>Le point d'arrivée</Emphasis> — la posture que l'on cherche à rejoindre.</>,
          <><Emphasis>Les conditions</Emphasis> — ce qui rend le déplacement possible.</>,
          <><Emphasis>Les obstacles</Emphasis> — ce qui le freine ou l'empêche.</>,
          <><Emphasis>Les étapes</Emphasis> — les moments intermédiaires par lesquels on passe.</>,
          <><Emphasis>La durée typique</Emphasis> — semaines, mois, années.</>,
        ]} />

        <H2>Trajectoires et illusions</H2>

        <P>
          Une trajectoire n'est pas un chemin balisé. C'est une <Emphasis>hypothèse</Emphasis> : si ces conditions
          sont réunies, et si ces obstacles sont travaillés, alors le déplacement devient possible.
        </P>

        <Callout type="info">
          L'atlas ne propose pas de trajectoire idéale. Il propose des trajectoires documentées, dont on connaît
          les conditions et les obstacles. Chaque acteur choisit celles qui lui paraissent pertinentes.
        </Callout>

        <H2>Comment lire une trajectoire</H2>

        <P>
          Une trajectoire s'affiche dans la vue 3D comme une ligne pointillée reliant deux archétypes. En la
          sélectionnant, vous accédez à sa fiche complète : conditions, obstacles, étapes, durée, et les concepts
          théoriques qui la fondent.
        </P>

        <Example label="POINT D'ATTENTION">
          Une trajectoire qui échoue n'est pas un échec. C'est une information : les conditions n'étaient pas
          réunies, ou les obstacles n'ont pas été travaillés. L'atlas n'attribue pas de faute.
        </Example>
      </>
    ),
  },

  // ═══════════════════════════════════════════════════════════════════════════
  // MODULE 04 — Les scénarios
  // ═══════════════════════════════════════════════════════════════════════════
  {
    id: '04',
    title: 'Les scénarios',
    subtitle: 'Situations décisionnelles typées',
    profiles: ['apprenant', 'formateur'],
    duration: '6 min',
    content: () => (
      <>
        <H1>Les scénarios</H1>

        <P lead>
          Un scénario est une <Emphasis>situation décisionnelle typée</Emphasis>. Il décrit un contexte, une
          question épistémologique, et la manière dont chaque posture se comporte dans cette situation.
        </P>

        <H2>Ce qu'un scénario décrit</H2>

        <List items={[
          <><Emphasis>Un contexte</Emphasis> — le cadre général de la situation.</>,
          <><Emphasis>Une question épistémologique</Emphasis> — ce que la situation demande de trancher.</>,
          <><Emphasis>Les dimensions convoquées</Emphasis> — les axes de l'atlas les plus sollicités.</>,
          <><Emphasis>Les comportements par posture</Emphasis> — comment chaque archétype réagit.</>,
          <><Emphasis>Les postures durables</Emphasis> — celles qui tiennent dans cette situation.</>,
          <><Emphasis>Les postures critiques</Emphasis> — celles qui s'y fragilisent.</>,
        ]} />

        <H2>Scénarios génériques et projections</H2>

        <P>
          Les scénarios de l'atlas sont <Emphasis>génériques</Emphasis>. Chacun est décliné en{' '}
          <Emphasis>projections</Emphasis> dans les domaines d'application. Une même structure décisionnelle
          — par exemple <Emphasis>décider sous pression temporelle</Emphasis> — s'incarne différemment en
          médecine d'urgence, en décision tactique, ou en arbitrage financier.
        </P>

        <Example label="EXEMPLE">
          Le scénario <Emphasis>Incertitude temporelle</Emphasis> se projette en{' '}
          <Emphasis color="#ef4444">triage d'urgence</Emphasis>, en{' '}
          <Emphasis color="#22d3ee">décision tactique sous feu</Emphasis>, en{' '}
          <Emphasis color="#a78bfa">décision en référé</Emphasis>, ou en{' '}
          <Emphasis color="#f97316">arbitrage sous volatilité</Emphasis>. La structure de la décision reste la
          même ; le contexte et les conséquences changent.
        </Example>

        <H2>Ce que les scénarios permettent</H2>

        <List items={[
          <>Comprendre comment une posture se comporte dans une situation donnée.</>,
          <>Identifier les postures qui fragilisent la décision.</>,
          <>Documenter une trajectoire décisionnelle pour analyse ultérieure.</>,
        ]} />
      </>
    ),
  },

  // ═══════════════════════════════════════════════════════════════════════════
  // MODULE 05 — Les tensions
  // ═══════════════════════════════════════════════════════════════════════════
  {
    id: '05',
    title: 'Les tensions',
    subtitle: 'Conflits intrinsèques entre axes',
    profiles: ['formateur', 'auditeur'],
    duration: '4 min',
    content: () => (
      <>
        <H1>Les tensions</H1>

        <P lead>
          Certaines positions sont <Emphasis>intrinsèquement inconfortables</Emphasis> : elles exigent beaucoup
          sur un axe et peu sur un autre, alors que les deux sont en relation. L'atlas appelle cela des tensions.
        </P>

        <H2>Trois tensions structurelles</H2>

        <H2>Contrôle et temps long</H2>

        <P>
          Le contrôle exige une réponse immédiate. Le temps long demande de la patience. Un acteur qui cherche à
          maîtriser chaque variable ne peut pas, simultanément, cultiver un projet à long terme. La tension est
          réelle, pas psychologique.
        </P>

        <H2>Mesure et accompagnement</H2>

        <P>
          Évaluer produit de la hiérarchie. Accompagner produit de l'autonomie. Un même acteur ne peut pas, dans
          le même geste, juger et révéler.
        </P>

        <H2>Erreur et système</H2>

        <P>
          Sanctionner l'erreur cache les signaux faibles. Voir le système exige de les accueillir. Une organisation
          qui punit les erreurs cesse de voir les signaux qui annoncent les crises.
        </P>

        <Callout type="info">
          Une tension n'est pas un défaut. C'est une <Emphasis>réalité structurelle</Emphasis>. L'atlas ne propose
          pas de la résoudre, mais de la reconnaître et de l'habiter.
        </Callout>

        <H2>Comment lire une tension</H2>

        <P>
          Chaque tension est documentée avec un exemple et une <Emphasis>voie de résolution partielle</Emphasis> :
          non pas une solution qui supprimerait la tension, mais une manière de la tenir sans s'y perdre.
        </P>
      </>
    ),
  },

  // ═══════════════════════════════════════════════════════════════════════════
  // MODULE 06 — Se positionner
  // ═══════════════════════════════════════════════════════════════════════════
  {
    id: '06',
    title: 'Se positionner',
    subtitle: 'Comment utiliser l\'éditeur de position',
    profiles: ['apprenant', 'formateur'],
    duration: '4 min',
    content: () => (
      <>
        <H1>Se positionner</H1>

        <P lead>
          Se positionner consiste à situer votre posture actuelle sur chacun des six axes. Ce n'est pas un test,
          c'est un exercice de lucidité.
        </P>

        <H2>Deux manières de se positionner</H2>

        <H2>Par les curseurs</H2>

        <P>
          Le bouton <Emphasis color="#a78bfa">ME POSITIONNER</Emphasis> ouvre un panneau dans la colonne de
          droite. Chaque axe dispose d'un curseur gradué de 0 à 100 %. Positionnez chaque curseur à l'endroit
          qui décrit le mieux votre pratique habituelle.
        </P>

        <H2>Par le déplacement direct</H2>

        <P>
          En activant le mode <Emphasis color="#22d3ee">DÉPLACER</Emphasis>, vous pouvez cliquer et glisser
          votre point dans la vue 3D. Trois des six axes sont alors ajustés simultanément : posture, temps,
          autre. Les trois autres axes restent réglables par les curseurs.
        </P>

        <Callout type="warning">
          Le déplacement dans l'espace 3D ajuste trois axes sur six. C'est une contrainte géométrique, pas un
          choix de conception. Pour un positionnement précis, utilisez les curseurs.
        </Callout>

        <H2>Reconnaître l'archétype le plus proche</H2>

        <P>
          Une fois votre position établie, l'atlas calcule l'archétype le plus proche. Il ne s'agit pas d'une
          classification, mais d'une <Emphasis>indication de proximité</Emphasis> : quel ensemble de postures
          cohérentes votre position actuelle rejoint le plus.
        </P>

        <Example label="EXEMPLE">
          Si votre position est proche de l'archétype <Emphasis color="#22d3ee">Architecte</Emphasis>, cela ne
          signifie pas que vous êtes un architecte. Cela signifie que votre position actuelle partage des
          caractéristiques avec cette configuration. Vous pouvez la faire évoluer.
        </Example>

        <H2>Re-positionnement dans le temps</H2>

        <P>
          Se positionner une fois n'a pas grande valeur. Se positionner à intervalles réguliers — à un mois,
          à six mois, à un an — permet de voir si la trajectoire évolue dans la direction souhaitée.
        </P>
      </>
    ),
  },

  // ═══════════════════════════════════════════════════════════════════════════
  // MODULE 07 — Jouer un scénario
  // ═══════════════════════════════════════════════════════════════════════════
  {
    id: '07',
    title: 'Jouer un scénario',
    subtitle: 'Comprendre ce qu\'une posture produit',
    profiles: ['apprenant', 'formateur'],
    duration: '5 min',
    content: () => (
      <>
        <H1>Jouer un scénario</H1>

        <P lead>
          Le mode <Emphasis color="#f97316">Scénario</Emphasis> permet d'explorer ce que chaque posture produit
          dans une situation décisionnelle donnée. Ce n'est pas un jeu — c'est un exercice de compréhension.
        </P>

        <H2>Étapes</H2>

        <List items={[
          <>Sélectionnez un <Emphasis>domaine</Emphasis> dans l'en-tête, ou laissez « tous domaines » pour voir l'ensemble.</>,
          <>Sélectionnez un <Emphasis>scénario</Emphasis> dans la liste du panneau gauche.</>,
          <>Les archétypes se <Emphasis>recolorent</Emphasis> dans la vue 3D selon leur comportement dans ce scénario.</>,
          <>Cliquez sur un scénario ou un archétype pour voir sa fiche détaillée.</>,
        ]} />

        <H2>Lecture des couleurs</H2>

        <List items={[
          <><Emphasis color="#34d399">Vert</Emphasis> — posture durable dans ce scénario.</>,
          <><Emphasis color="#fbbf24">Ambre</Emphasis> — posture à risque modéré.</>,
          <><Emphasis color="#ef4444">Rouge</Emphasis> — posture critique, qui fragilise la décision.</>,
        ]} />

        <H2>Votre posture dans le scénario</H2>

        <P>
          Si vous avez préalablement défini votre position, l'atlas calcule automatiquement l'archétype le plus
          proche de vous, et affiche dans la fiche de scénario le comportement qui correspond à cette proximité.
          Vous voyez alors, de manière située, ce que <Emphasis>votre posture</Emphasis> produit dans ce scénario.
        </P>

        <Callout type="info">
          L'atlas ne dit pas que votre posture est mauvaise. Il indique ce qu'elle produit dans un contexte donné,
          et à quel niveau de risque.
        </Callout>

        <H2>Enregistrer un test</H2>

        <P>
          Si une session de trace est active, trois boutons apparaissent en bas de la vue 3D :{' '}
          <Emphasis color="#34d399">RÉUSSI</Emphasis>, <Emphasis color="#fbbf24">PARTIEL</Emphasis>,{' '}
          <Emphasis color="#ef4444">ÉCHEC</Emphasis>. Ils permettent d'enregistrer le résultat du scénario dans
          la trace de session.
        </P>
      </>
    ),
  },

  // ═══════════════════════════════════════════════════════════════════════════
  // MODULE 08 — Enregistrer une trace
  // ═══════════════════════════════════════════════════════════════════════════
  {
    id: '08',
    title: 'Enregistrer une trace',
    subtitle: 'Documenter une trajectoire décisionnelle',
    profiles: ['apprenant', 'formateur', 'auditeur'],
    duration: '6 min',
    content: () => (
      <>
        <H1>Enregistrer une trace</H1>

        <P lead>
          Une trace est un <Emphasis>document structuré</Emphasis> qui enregistre une session d'exploration ou
          de test : position initiale, positions successives, scénarios testés, durée, et éventuellement une
          évaluation.
        </P>

        <H2>À quoi sert une trace</H2>

        <List items={[
          <>Documenter une progression dans le temps.</>,
          <>Analyser les scénarios où une posture a été mise en difficulté.</>,
          <>Produire une preuve d'acquisition, dans un cadre de certification.</>,
          <>Permettre un retour d'expérience structuré.</>,
        ]} />

        <H2>Démarrer une session</H2>

        <P>
          Dans le mode <Emphasis color="#22d3ee">Trace</Emphasis>, un panneau de démarrage apparaît dans la
          colonne de gauche. Il demande quatre informations :
        </P>

        <List items={[
          <><Emphasis>Acteur</Emphasis> — le libellé affiché (peut être anonyme).</>,
          <><Emphasis>Référentiel</Emphasis> — le cadre dans lequel la session s'inscrit, sous forme de chaîne libre.</>,
          <><Emphasis>Domaine</Emphasis> — le domaine d'application, ou aucun.</>,
          <><Emphasis>Profil</Emphasis> — apprenant, formateur, auditeur.</>,
        ]} />

        <P>
          Une fois ces informations saisies, cliquez sur <Emphasis>DÉMARRER</Emphasis>. La position actuelle de
          votre acteur est enregistrée comme point initial.
        </P>

        <H2>Pendant la session</H2>

        <List items={[
          <>Chaque déplacement de votre acteur est enregistré automatiquement.</>,
          <>Chaque scénario testé peut être enregistré via les trois boutons d'évaluation.</>,
          <>Le panneau gauche affiche en temps réel le nombre de positions enregistrées.</>,
        ]} />

        <H2>Terminer la session</H2>

        <P>
          Le bouton <Emphasis color="#34d399">TERMINER LA SESSION</Emphasis> clôture la trace, calcule la durée
          totale, et signe la trace pour garantir son intégrité.
        </P>

        <H2>Signature et intégrité</H2>

        <P>
          La signature n'est pas une signature cryptographique forte. C'est un <Emphasis>checksum</Emphasis> qui
          détecte les modifications accidentelles. Il est suffisant pour un usage local, mais pas pour un usage
          réglementaire.
        </P>

        <Callout type="info">
          Une trace signée peut être vérifiée à tout moment. Si la vérification échoue, la trace a été modifiée
          après signature.
        </Callout>

        <H2>Sauvegarde et export</H2>

        <List items={[
          <><Emphasis>ENREGISTRER</Emphasis> — sauvegarde la trace dans le stockage local du navigateur.</>,
          <><Emphasis>EXPORTER</Emphasis> — produit un fichier JSON structuré, téléchargeable.</>,
        ]} />

        <P>
          Le format JSON est documenté dans le schéma de traçabilité. Il est agnostique au référentiel : le
          référentiel est stocké comme chaîne libre, sans interprétation par l'atlas.
        </P>
      </>
    ),
  },

  // ═══════════════════════════════════════════════════════════════════════════
  // MODULE 09 — Animer une session
  // ═══════════════════════════════════════════════════════════════════════════
  {
    id: '09',
    title: 'Animer une session',
    subtitle: 'Usage formateur',
    profiles: ['formateur'],
    duration: '7 min',
    content: () => (
      <>
        <H1>Animer une session</H1>

        <P lead>
          L'atlas n'est pas un support d'exposé. C'est un <Emphasis>terrain d'exploration</Emphasis>. Le rôle du
          formateur n'est pas d'expliquer la carte, mais d'accompagner chaque apprenant dans la lecture de sa
          propre position.
        </P>

        <H2>Préparer une session</H2>

        <List items={[
          <>Choisir un <Emphasis>domaine</Emphasis> pertinent pour le public (médical, militaire, éducatif, etc.).</>,
          <>Sélectionner un ou deux <Emphasis>scénarios</Emphasis> qui font écho à des situations vécues.</>,
          <>Prévoir un temps de <Emphasis>positionnement individuel</Emphasis> avant tout échange collectif.</>,
        ]} />

        <Callout type="warning">
          Le positionnement individuel doit précéder l'échange collectif. Sinon, les apprenants se positionnent
          par rapport au groupe, et l'exercice perd sa valeur.
        </Callout>

        <H2>Structure d'animation recommandée</H2>

        <H2>Temps 1 — Présentation du modèle</H2>

        <P>
          Présentez les six axes et le principe de position. Ne cherchez pas à tout expliquer. L'exploration
          fera le travail.
        </P>

        <H2>Temps 2 — Positionnement individuel</H2>

        <P>
          Chaque apprenant se positionne, à son rythme, sur les six axes. Aucun échange pendant ce temps.
          Comptez 10 à 15 minutes selon le public.
        </P>

        <H2>Temps 3 — Exploration en binôme</H2>

        <P>
          En binôme, chaque apprenant explique sa position à l'autre. L'objectif n'est pas de se justifier, mais
          de rendre sa position lisible pour quelqu'un d'autre.
        </P>

        <H2>Temps 4 — Test de scénarios</H2>

        <P>
          Le groupe teste collectivement un ou deux scénarios. Les postures durables et critiques sont discutées
          à partir des observations faites, pas à partir de la théorie.
        </P>

        <H2>Temps 5 — Synthèse et trajectoires</H2>

        <P>
          Chaque apprenant identifie une trajectoire qu'il souhaiterait explorer, avec ses conditions et ses
          obstacles. Cette trajectoire devient un point de départ pour la suite.
        </P>

        <H2>Points d'attention</H2>

        <List items={[
          <>Ne jamais qualifier une posture de « bonne » ou « mauvaise ».</>,
          <>Ne jamais réduire un apprenant à son archétype le plus proche.</>,
          <>Laisser le silence travailler : l'atlas demande un temps d'intériorisation.</>,
          <>Ne pas chercher à conclure trop vite : les trajectoires se construisent dans la durée.</>,
        ]} marker="!" markerColor="#fbbf24" />
      </>
    ),
  },

  // ═══════════════════════════════════════════════════════════════════════════
  // MODULE 10 — Lire une trace
  // ═══════════════════════════════════════════════════════════════════════════
  {
    id: '10',
    title: 'Lire une trace',
    subtitle: 'Usage auditeur',
    profiles: ['auditeur'],
    duration: '5 min',
    content: () => (
      <>
        <H1>Lire une trace</H1>

        <P lead>
          Une trace est un document qui décrit une session. Sa lecture doit permettre de comprendre ce qui a
          été fait, dans quel cadre, avec quel résultat, sans avoir besoin d'interroger l'acteur.
        </P>

        <H2>Structure d'une trace</H2>

        <H2>Section session</H2>

        <List items={[
          <>Identifiant unique de session.</>,
          <>Identité de l'acteur, ou mention anonyme.</>,
          <>Horodatage de début et de fin.</>,
          <>Référentiel de certification, sous forme de chaîne libre.</>,
          <>Profil utilisé.</>,
          <>Domaine d'application.</>,
        ]} />

        <H2>Section trajectoire</H2>

        <List items={[
          <>Position initiale et position finale, sur les six axes.</>,
          <>Historique complet des positions, horodaté.</>,
          <>Éventuelle trajectoire suivie, si elle correspond à une trajectoire documentée.</>,
          <>Étapes atteintes, si elles ont été marquées.</>,
          <>Durée totale.</>,
          <>Scénarios testés, avec leurs résultats.</>,
        ]} />

        <H2>Section preuve</H2>

        <List items={[
          <>Référence de compétence, sous forme de chaîne libre.</>,
          <>Niveau atteint, selon quatre niveaux : novice, intermédiaire, avancé, expert.</>,
          <>Observations complémentaires.</>,
          <>Indicateur de préparation à la certification.</>,
          <>Signature d'intégrité.</>,
        ]} />

        <H2>Vérification</H2>

        <P>
          Chaque trace peut être vérifiée pour s'assurer qu'elle n'a pas été modifiée après signature. Une trace
          dont la signature ne correspond pas doit être considérée comme <Emphasis>non fiable</Emphasis>.
        </P>

        <Callout type="warning">
          La signature actuelle est un checksum, pas une signature cryptographique. Elle détecte les
          modifications accidentelles. Elle ne protège pas contre une modification intentionnelle.
        </Callout>
      </>
    ),
  },

  // ═══════════════════════════════════════════════════════════════════════════
  // MODULE 11 — Produire une preuve
  // ═══════════════════════════════════════════════════════════════════════════
  {
    id: '11',
    title: 'Produire une preuve',
    subtitle: 'Usage certificateur',
    profiles: ['auditeur'],
    duration: '5 min',
    content: () => (
      <>
        <H1>Produire une preuve</H1>

        <P lead>
          L'atlas ne certifie rien par lui-même. Il produit des traces documentées qui peuvent servir de base à
          une décision de certification, prise par l'autorité compétente.
        </P>

        <H2>Ce qu'une trace prouve</H2>

        <List items={[
          <>Qu'une session a eu lieu, à une date donnée, avec un acteur identifié.</>,
          <>Qu'un positionnement a été effectué, sur les six axes.</>,
          <>Que des scénarios ont été testés, avec des résultats documentés.</>,
          <>Qu'une progression a eu lieu entre la position initiale et la position finale.</>,
        ]} />

        <H2>Ce qu'une trace ne prouve pas</H2>

        <List items={[
          <>Que la posture finale est meilleure que la posture initiale. L'atlas ne juge pas.</>,
          <>Que l'acteur a compris quelque chose. La trace documente des faits, pas des intentions.</>,
          <>Que la compétence est acquise. Cette décision revient au certificateur.</>,
        ]} marker="—" markerColor="#4a5578" />

        <H2>Critères de lecture</H2>

        <P>
          Trois critères peuvent être utilisés pour lire une trace :
        </P>

        <List items={[
          <><Emphasis>Continuité</Emphasis> — la progression a-t-elle été régulière, ou concentrée en fin de session ?</>,
          <><Emphasis>Cohérence</Emphasis> — les positions successives forment-elles une trajectoire identifiable ?</>,
          <><Emphasis>Résistance</Emphasis> — l'acteur a-t-il tenu sa posture dans les scénarios critiques ?</>,
        ]} />

        <H2>Niveau atteint</H2>

        <P>
          Le niveau atteint (novice, intermédiaire, avancé, expert) est un jugement qui appartient au
          certificateur, pas à l'atlas. L'atlas fournit les données ; le certificateur pose le jugement.
        </P>

        <Callout type="info">
          La séparation entre <Emphasis>documentation</Emphasis> et <Emphasis>certification</Emphasis> est
          volontaire. Elle garantit que la certification reste du ressort de l'autorité compétente, quel que
          soit le référentiel.
        </Callout>
      </>
    ),
  },

  // ═══════════════════════════════════════════════════════════════════════════
  // MODULE 12 — Corpus théorique
  // ═══════════════════════════════════════════════════════════════════════════
  {
    id: '12',
    title: 'Corpus théorique',
    subtitle: 'Les fondements conceptuels',
    profiles: ['formateur', 'auditeur'],
    duration: '8 min',
    content: () => (
      <>
        <H1>Corpus théorique</H1>

        <P lead>
          L'atlas ne s'invente pas. Il mobilise un ensemble de concepts issus de la philosophie, des sciences
          des systèmes, et des sciences de gestion. Ce module présente les références qui fondent le modèle.
        </P>

        <H2>Incertitude et navigation</H2>

        <Quote source="Edgar Morin, 1990">
          L'incertitude n'est pas un défaut à corriger mais la condition même de la connaissance.
        </Quote>

        <Quote source="Peter Senge, 1990">
          Les systèmes complexes se naviguent, ils ne se contrôlent pas.
        </Quote>

        <H2>Temporalité</H2>

        <Quote source="François Jullien, 2001">
          La transformation durable passe par la patience et la maturation lente.
        </Quote>

        <Quote source="Nassim Taleb, 2012">
          L'optimisation locale détruit le cumul. Le cumul tolère l'imperfection locale.
        </Quote>

        <H2>Accompagnement</H2>

        <Quote source="Maela Paul, 2004">
          Accompagner, ce n'est pas prescrire — c'est rendre l'autre capable.
        </Quote>

        <H2>Mesure</H2>

        <Quote source="Charles Goodhart, 1975">
          Quand une mesure devient un objectif, elle cesse d'être une bonne mesure.
        </Quote>

        <Quote source="James Scott, 1998">
          Ce qui est mesuré est ce qui devient visible — et cela transforme le monde.
        </Quote>

        <H2>Erreur et apprentissage</H2>

        <Quote source="Gaston Bachelard, 1938">
          L'erreur est un moment nécessaire de la connaissance.
        </Quote>

        <Quote source="Igor Ansoff, 1975">
          Les transformations majeures annoncent leur arrivée par des signaux faibles.
        </Quote>

        <H2>Pensée systémique</H2>

        <Quote source="Donella Meadows, 2008">
          Les systèmes produisent leurs comportements à travers leurs structures.
        </Quote>

        <Quote source="Jay Forrester, 1961">
          Les systèmes complexes sont régis par des boucles qui amplifient ou stabilisent.
        </Quote>

        <Callout type="info">
          Ces références ne sont pas des arguments d'autorité. Elles indiquent les traditions intellectuelles
          dans lesquelles l'atlas s'inscrit, et permettent à un utilisateur curieux d'approfondir.
        </Callout>
      </>
    ),
  },

  // ═══════════════════════════════════════════════════════════════════════════
  // MODULE 13 — Parcours par profil
  // ═══════════════════════════════════════════════════════════════════════════
  {
    id: '13',
    title: 'Parcours de lecture',
    subtitle: 'Quels modules lire, selon votre profil',
    profiles: ['apprenant', 'formateur', 'auditeur'],
    duration: '2 min',
    content: () => (
      <>
        <H1>Parcours de lecture</H1>

        <P lead>
          Ce livret est modulaire. Chaque module est autonome, mais l'ordre de lecture peut être adapté à votre
          profil et à votre objectif.
        </P>

        <H2>Parcours apprenant</H2>

        <List items={[
          <>Module 00 — Le modèle en une page</>,
          <>Module 01 — Les six axes</>,
          <>Module 02 — Les postures</>,
          <>Module 06 — Se positionner</>,
          <>Module 07 — Jouer un scénario</>,
          <>Module 08 — Enregistrer une trace</>,
        ]} />

        <H2>Parcours formateur</H2>

        <List items={[
          <>Module 00 — Le modèle en une page</>,
          <>Module 01 — Les six axes</>,
          <>Module 02 — Les postures</>,
          <>Module 03 — Les trajectoires</>,
          <>Module 04 — Les scénarios</>,
          <>Module 05 — Les tensions</>,
          <>Module 09 — Animer une session</>,
          <>Module 12 — Corpus théorique</>,
        ]} />

        <H2>Parcours auditeur</H2>

        <List items={[
          <>Module 00 — Le modèle en une page</>,
          <>Module 08 — Enregistrer une trace</>,
          <>Module 10 — Lire une trace</>,
          <>Module 11 — Produire une preuve</>,
          <>Module 12 — Corpus théorique</>,
        ]} />

        <Callout type="info">
          Ces parcours sont indicatifs. Un module reste accessible à tout moment, depuis n'importe quel écran de
          l'atlas, en cliquant sur l'icône contextuelle correspondante.
        </Callout>
      </>
    ),
  },
];


export const livretContextMap = {
  // Cartes d'inspection
  axis: '01',
  archetype: '02',
  trajectory: '03',
  scenario: '04',
  tension: '05',

  // Actions et panneaux
  positionEditor: '06',
  scenarioTest: '07',
  traceStart: '08',
  traceRead: '10',
  traceCertify: '11',

  // Modes
  modeAtelier: '01',
  modeScenario: '04',
  modeTrace: '08',

  // Méta
  model: '00',
  theories: '12',
  parcours: '13',
};

export const getModuleByContext = (context) => {
  const id = livretContextMap[context];
  return id ? getModuleById(id) : null;
};


export const getModuleById = (id) => {
  return livretModules.find(m => m.id === id) || null;
};

export const getModulesForProfile = (profile) => {
  return livretModules.filter(m => m.profiles?.includes(profile));
};

export default livretModules;